import { Box, Grid, IconButton, TextField } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Business } from "../../../assets/SideBarIcons/business.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import IntegrationTabs from "./IntegrationTabs";
import Loader from "layout/Loader";
import { BusinessProfileService, ClientProfileService } from "subModule/src/core/services";
import { GetBusinessIntegrationByIdResponse } from "subModule/src/services/business/interface";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { Add, Remove } from "@mui/icons-material";
import { useBusinessTabHeader } from "Helpers/useBusinessTabHeader";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { useGetHostingDetailsService } from "subModule/src/services/client/useClient";
// import ComponentSubheader from "common/ComponentHeaderTwo/ComponentSubheader";
// import WhatsAppIcon from "@mui/icons-material/WhatsApp";
interface FormData {
  [key: string]: any;
}

const WHATSAPP_CONFIG_FIELDS = [
  "configValue.phone_number_id",
  "configValue.wabaId",
  "configValue.business_account_id",
  "configValue.api_version",
  "configValue.access_token",
  "templates",
];
const PAYMENT_CONFIG_FIELDS = [
  "configValue.secretKey",
  "configValue.publicKey",
  "configValue.merchantId",
];
const TRONSTRIDE_CONFIG_FIELDS = [
  "configValue.companyId",
  "configValue.apiKey",
];
const FLEETOS_CONFIG_FIELDS = [
  "configValue.auto",
  "configValue.cashCustomers",
  "configValue.contractCustomers",
  "configValue.accountHostingId",
];

export default function IntegrationModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const { businessName } = useBusinessTabHeader(params?.id);
  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { labelKey: "businessSetup" },
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        ...(businessName ? { label: businessName } : {}),
        href: params?.id ? `/business/${params.id}` : undefined,
      },
      {
        labelKey: "integrations",
        href: params?.id ? `/business/${params.id}/integrations` : undefined,
      },
      { labelKey: params?.integrationId ? "Details" : "create" },
    ],
    [businessName, params?.id, params?.integrationId],
  );
  const {
    register,
    unregister,
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      type: { value: 1, label: t("whatsapp") },
      templates: [{ key: null, value: "" }],
      configValue: {
        auto: false,
        cashCustomers: false,
        contractCustomers: false,
        accountHostingId: null,
      },
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "templates",
  });
  const [loading, setLoading] = useState<boolean>(
    params?.integrationId ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.integrationId ? false : true,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const pendingHostingIdRef = useRef<number | null>(null);
  const { data: hostingDetailsData, refetch: hostingRefetch } =
    useGetHostingDetailsService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const hostingList = (hostingDetailsData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.title }),
  );
  const integrationTypes = [
    { value: 1, label: t("whatsapp") },
    { value: 2, label: t("payment") },
    { value: 3, label: t("Tronstride") },
    { value: 4, label: t("FleetOS") },
  ];
  const whatsappTemplates = [
    { value: "1", label: t("invoice") },
    { value: "2", label: t("paymentReminder") },
    { value: "3", label: t("serviceReminder") },
    { value: "4", label: t("jobcardApproval") },
    { value: "5", label: t("inspection") },
    { value: "6", label: t("consolidated") },
    { value: "7", label: t("customerFollowUp") },
    { value: "8", label: t("customerLost") },
    { value: "9", label: t("customerAtRisk") },
  ];
  const templateTitlePlaceholderByType: Record<string, string> = {
    "1": "phWhatsappInvoiceTemplate",
    "2": "phWhatsappPaymentReminderTemplate",
    "3": "phWhatsappServiceReminderTemplate",
    "4": "phWhatsappJobcardApprovalTemplate",
    "5": "phWhatsappInspectionTemplate",
    "6": "phWhatsappConsolidatedTemplate",
    "7": "phWhatsappCustomerFollowUpTemplate",
    "8": "phWhatsappCustomerLostTemplate",
    "9": "phWhatsappCustomerAtRiskTemplate",
  };
  const getTemplateTitlePlaceholder = (templateKey?: string | null) =>
    t(
      (templateKey && templateTitlePlaceholderByType[templateKey]) ||
        "phWhatsappTemplateName",
    );

  useEffect(() => {
    if (params?.integrationId) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.integrationId) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);

  // After hosting list loads, replace raw accountHostingId with title
  useEffect(() => {
    const pendingId = pendingHostingIdRef.current;
    if (pendingId == null) return;

    const applyHostingOption = (option: { value: any; label: string }) => {
      setValue("configValue.accountHostingId", option, {
        shouldDirty: false,
        shouldValidate: true,
      });
      pendingHostingIdRef.current = null;
    };

    const match = hostingList.find(
      (item: any) => Number(item.value) === Number(pendingId),
    );
    if (match?.label) {
      applyHostingOption(match);
      return;
    }

    // List loaded but id not on this page — resolve by id once
    if (hostingDetailsData != null) {
      ClientProfileService.getHostingDetailsById(pendingId)
        .then((byIdRes) => {
          if (pendingHostingIdRef.current !== pendingId) return;
          if (byIdRes?.data?.id != null) {
            applyHostingOption({
              value: byIdRes.data.id,
              label: byIdRes.data.title || String(byIdRes.data.id),
            });
          }
        })
        .catch((err) => console.error("===", err));
    }
  }, [hostingDetailsData, hostingList]);

  const handleHostingDropdownActions = (
    action: "open" | "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "open") {
      setSearchLoading("hostingDetails");
      hostingRefetch().finally(() => setSearchLoading(null));
      return;
    }
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search || "");
      if (search) setSearchLoading("hostingDetails");
    } else {
      setPageNo((prev) => prev + 1);
      setSearchLoading("hostingDetails");
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      hostingRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };

  const getByID = () => {
    BusinessProfileService.getBusinesIntegrationByID(
      +(params as any)?.integrationId,
    )
      .then((res: GetBusinessIntegrationByIdResponse) => {
        if (res.success) {
          const configValue = res.data?.configValue || {};
          const rawHostingId = (configValue as any)?.accountHostingId;
          if (rawHostingId != null && rawHostingId !== "") {
            pendingHostingIdRef.current = Number(rawHostingId);
          } else {
            pendingHostingIdRef.current = null;
          }
          reset({
            ...(res?.data || {}),
            type: {
              value: res.data?.type,
              label:
                integrationTypes.find((v) => v.value === res.data?.type)
                  ?.label || res.data?.type,
            },
            templates: Object.entries(res.data.templates || {}).map(
              ([key, value]: any) => ({
                key: whatsappTemplates.find((t) => t.value === key),
                value: value,
              }),
            ),
            configValue: {
              ...configValue,
              accountHostingId:
                rawHostingId != null && rawHostingId !== ""
                  ? {
                      value: Number(rawHostingId),
                      label: String(rawHostingId),
                    }
                  : null,
            },
          });
          if (res?.data?.type === 2) unregister("configValue.access_token");
          setLoading(false);
          if (rawHostingId != null && rawHostingId !== "") {
            setSearchLoading("hostingDetails");
            hostingRefetch().finally(() => setSearchLoading(null));
          }
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const buildPayload = (data: any) => {
    const type = data.type?.value || null;
    const payload: any = {
      title: data.title,
      description: data.description,
      type,
      businessId: Number(params?.id),
    };
    if (type === 1) {
      payload.configValue = {
        phone_number_id: data.configValue?.phone_number_id,
        business_account_id: data.configValue?.business_account_id,
        api_version: data.configValue?.api_version,
        wabaId: data.configValue?.wabaId,
        access_token: data.configValue?.access_token,
      };
      payload.templates = data?.templates?.reduce((acc: any, item: any) => {
        if (item?.key?.value) acc[item.key.value] = item.value;
        return acc;
      }, {});
    } else if (type === 2) {
      payload.configValue = {
        secretKey: data.configValue?.secretKey,
        publicKey: data.configValue?.publicKey,
      };
    } else if (type === 3) {
      payload.configValue = {
        companyId: data.configValue?.companyId,
        apiKey: data.configValue?.apiKey,
      };
    } else if (type === 4) {
      payload.configValue = {
        auto: !!data.configValue?.auto,
        cashCustomers: !!data.configValue?.cashCustomers,
        contractCustomers: !!data.configValue?.contractCustomers,
        accountHostingId: data.configValue?.accountHostingId?.value ?? null,
      };
    }
    return payload;
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const payload = buildPayload(data);
    if (params?.integrationId) {
      BusinessProfileService.updateBusinessIntegration(
        Number(params?.integrationId),
        payload,
      )
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("integration") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      BusinessProfileService.createBusinessIntegration(payload)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("integration") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(`/business/${params?.id}/integrations`)}
        onSubmit={handleSubmit(onSubmit)}
        heading={t("Businessses")}
        icon={<Business className="text-black w-[30px] h-[30px]" />}
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
        isBtnEnabled={IsBtnEnabled}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className="flex">
        <IntegrationTabs params={params} selectedIndex={3} />
        {loading ? (
          <Loader />
        ) : (
          <div className="w-[100%] mt-[10px]">
            {/* <ComponentSubheader
              showBtn={false}
              showSaveBtn={false}
              onClick={() => navigate(-1)}
              onSubmit={handleSubmit(onSubmit)}
              heading={
                params?.integrationId
                  ? t("editIntegration")
                  : t("addIntegration")
              }
              icon={
                <WhatsAppIcon
                  sx={{ fill: "#000", width: "30px", height: "30px" }}
                />
              }
              btnText={"back"}
              btnType={"back"}
              isBtnEnabled={IsBtnEnabled}
              showCreateBtn={true}
            /> */}
            <SimpleModal
              visable={true}
              // padding={false}
              title={
                params?.integrationId
                  ? "editBusinessIntegration"
                  : "addBusinessPolicy"
              }
              isButtonVisable={true}
              isVisableBtn={IsBtnEnabled}
              btnclosetext="Back"
              buttonText={params?.integrationId ? "update" : "submit"}
              attBtnNotshow={false}
              notVisableBackbtn={true}
              showPortList={handleSubmit(onSubmit)}
              formSubmit={handleSubmit(onSubmit)}
              content={
                <Grid
                  container
                  component="form"
                  id="form"
                  rowSpacing={3}
                  columnSpacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="type"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("type") + " *"}
                          defaultPlaceholder="phSelectIntegrationType"
                          {...field}
                          options={integrationTypes}
                          isSearchable
                          onChange={(selectedOption: any) => {
                            field.onChange(selectedOption);
                            if (selectedOption?.value === 1) {
                              unregister([
                                ...PAYMENT_CONFIG_FIELDS,
                                ...TRONSTRIDE_CONFIG_FIELDS,
                                ...FLEETOS_CONFIG_FIELDS,
                              ]);
                            }
                            if (selectedOption?.value === 2) {
                              unregister([
                                ...WHATSAPP_CONFIG_FIELDS,
                                ...TRONSTRIDE_CONFIG_FIELDS,
                                ...FLEETOS_CONFIG_FIELDS,
                              ]);
                            }
                            if (selectedOption?.value === 3) {
                              unregister([
                                ...WHATSAPP_CONFIG_FIELDS,
                                ...PAYMENT_CONFIG_FIELDS,
                                ...FLEETOS_CONFIG_FIELDS,
                              ]);
                            }
                            if (selectedOption?.value === 4) {
                              unregister([
                                ...WHATSAPP_CONFIG_FIELDS,
                                ...PAYMENT_CONFIG_FIELDS,
                                ...TRONSTRIDE_CONFIG_FIELDS,
                              ]);
                            }
                          }}
                          error={(errors as any)?.type?.message as string}
                          isDisabled={(params as any)?.integrationId}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}></Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("title", {
                        validate: {
                          required: (value) =>
                            value.trim() !== "" || t("This field is required."),
                        },
                      })}
                      label={t("title") + " *"}
                      placeholder={t("phIntegrationTitle")}
                      title={t("title")}
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      {...register("description")}
                      label={t("description")}
                      placeholder={t("phIntegrationDescription")}
                      title={t("description")}
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={1}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  {watch("type")?.value === 1 ? (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("configValue.phone_number_id", {
                            required: t("This field is required."),
                          })}
                          label={t("phone_number_id") + " *"}
                          placeholder={t("phPhoneNumberId")}
                          title={t("phone_number_id")}
                          fullWidth
                          error={
                            !!(errors?.configValue as any)?.phone_number_id
                          }
                          helperText={
                            (errors?.configValue as any)?.phone_number_id
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("configValue.business_account_id", {
                            required: false,
                          })}
                          label={t("business_account_id")}
                          placeholder={t("phBusinessAccountId")}
                          title={t("business_account_id")}
                          fullWidth
                          error={
                            !!(errors?.configValue as any)?.business_account_id
                          }
                          helperText={
                            (errors?.configValue as any)?.business_account_id
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.api_version")}
                          label={t("api_version")}
                          placeholder={t("phApiVersion")}
                          title={t("api_version")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.api_version}
                          helperText={
                            (errors?.configValue as any)?.api_version
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("configValue.wabaId", {
                            required: false,
                          })}
                          label={t("wabaId")}
                          placeholder={t("phWabaId")}
                          title={t("wabaId")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.wabaId}
                          helperText={
                            (errors?.configValue as any)?.wabaId
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.access_token", {
                            validate: {
                              required: (value) =>
                                value.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("access_token") + " *"}
                          placeholder={t("phAccessToken")}
                          title={t("access_token")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.access_token}
                          helperText={
                            (errors?.configValue as any)?.access_token
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <h1
                          className="flex font-Saira font-[700] text-[17px] mb-0"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("templates")}
                        </h1>
                      </Grid>
                      {fields.map((field, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={field.id}
                          className="ms-0 mt-0"
                        >
                          <Grid item xs={12} md={3}>
                            <Controller
                              name={`templates.${index}.key`}
                              control={control}
                              defaultValue={null}
                              rules={{ required: t("This field is required.") }}
                              render={({ field }) => (
                                <DynamicSelect
                                  label={t("template") + " *"}
                                  defaultPlaceholder="phSelectWhatsappTemplate"
                                  {...field}
                                  options={whatsappTemplates.map((opt) => ({
                                    ...opt,
                                    isDisabled:
                                      watch("templates")
                                        ?.map((t: any) => t?.key?.value)
                                        ?.includes(opt.value) &&
                                      opt.value !== field.value?.value,
                                  }))}
                                  isClearable
                                  isSearchable
                                  onChange={(selectedOption: any) =>
                                    field.onChange(selectedOption)
                                  }
                                  error={
                                    (errors as any)?.templates?.[index]?.key
                                      ?.message as string
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label={t("title") + " *"}
                              InputLabelProps={{ shrink: true }}
                              placeholder={getTemplateTitlePlaceholder(
                                watch(`templates.${index}.key`)?.value,
                              )}
                              title={t("title")}
                              {...register(`templates.${index}.value`, {
                                validate: {
                                  required: (value) =>
                                    value.trim() !== "" ||
                                    t("This field is required."),
                                },
                              })}
                              fullWidth
                              error={
                                !!(errors as any)?.templates?.[index]?.value
                              }
                              helperText={
                                (errors as any)?.templates?.[index]?.value
                                  ?.message as string
                              }
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={2}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            {fields.length < whatsappTemplates.length && (
                              <IconButton
                                onClick={() => append({ key: null, value: "" })}
                                className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                              >
                                <Add style={{ color: themeColors?.primary }} />
                              </IconButton>
                            )}
                            {fields.length > 1 && (
                              <IconButton
                                onClick={() => remove(index)}
                                color="error"
                                className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                              >
                                <Remove />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </>
                  ) : null}
                  {watch("type")?.value === 2 ? (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.secretKey", {
                            validate: {
                              required: (value) =>
                                value.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("secretKey") + " *"}
                          placeholder={t("phSecretKey")}
                          title={t("secretKey")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.secretKey}
                          helperText={
                            (errors?.configValue as any)?.secretKey
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.publicKey", {
                            validate: {
                              required: (value) =>
                                value.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("publicKey") + " *"}
                          placeholder={t("phPublicKey")}
                          title={t("publicKey")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.publicKey}
                          helperText={
                            (errors?.configValue as any)?.publicKey
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {/* <Grid item xs={12} md={3}>
                                    <TextField
                                        type="number"
                                        {...register("configValue.merchantId", { 
                                            required: t('This field is required.'),
                                        })}
                                        label={t("merchantId") + ' *'}
                                        placeholder={t("merchantId")}
                                        title={t("merchantId")}
                                        fullWidth
                                        error={!!(errors?.configValue as any)?.merchantId}
                                        helperText={(errors?.configValue as any)?.merchantId?.message as string}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid> */}
                    </>
                  ) : null}
                  {watch("type")?.value === 3 ? (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.companyId", {
                            validate: {
                              required: (value) =>
                                value.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("companyId") + " *"}
                          placeholder={t("phTronstrideCompanyId")}
                          title={t("companyId")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.companyId}
                          helperText={
                            (errors?.configValue as any)?.companyId
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("configValue.apiKey", {
                            validate: {
                              required: (value) =>
                                value.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("apiKey") + " *"}
                          placeholder={t("phTronstrideApiKey")}
                          title={t("apiKey")}
                          fullWidth
                          error={!!(errors?.configValue as any)?.apiKey}
                          helperText={
                            (errors?.configValue as any)?.apiKey
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  ) : null}
                  {watch("type")?.value === 4 ? (
                    <>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="configValue.accountHostingId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("hostingDetails") + " *"}
                              defaultPlaceholder="phSelectHostingDetails"
                              {...field}
                              options={hostingList}
                              isSearchable
                              isClearable
                              onMenuOpen={() =>
                                handleHostingDropdownActions("open")
                              }
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={
                                (errors?.configValue as any)?.accountHostingId
                                  ?.message as string
                              }
                              isLoading={searchLoading === "hostingDetails"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleHostingDropdownActions(
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleHostingDropdownActions(
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(hostingDetailsData?.pages[0]?.data as any)
                                    ?.last
                                )
                                  handleHostingDropdownActions("loadNext");
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="configValue.auto"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              label="auto"
                              checked={!!field.value}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => field.onChange(event.target.checked)}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="configValue.cashCustomers"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              label="cashCustomers"
                              checked={!!field.value}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => field.onChange(event.target.checked)}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="configValue.contractCustomers"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              label="contractCustomers"
                              checked={!!field.value}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => field.onChange(event.target.checked)}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              }
            />
          </div>
        )}
      </Box>
    </>
  );
}
